import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CenterText.css';

// Interface para os segmentos de texto parseados
interface TextSegment {
  text: string;
  bold: boolean;
}

// Função para parsear texto com marcações **bold**
const parseFormattedText = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Adiciona texto normal antes do match
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        bold: false
      });
    }
    // Adiciona texto em negrito
    segments.push({
      text: match[1],
      bold: true
    });
    lastIndex = regex.lastIndex;
  }

  // Adiciona o resto do texto após o último match
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      bold: false
    });
  }

  return segments;
};

// Função para obter o texto puro (sem marcações)
const getPlainText = (text: string): string => {
  return text.replace(/\*\*(.+?)\*\*/g, '$1');
};

export interface CenterTextProps {
  text: string; // Texto a ser exibido (use **texto** para negrito)
  fontSize?: number; // Tamanho da fonte em pixels (padrão: 24)
  fontFamily?: string; // Família da fonte (padrão: 'Arial, sans-serif')
  color?: string; // Cor do texto (padrão: 'white')
  backgroundColor?: string; // Cor de fundo (padrão: 'rgba(0, 0, 0, 0.7)')
  position?: { x: number; y: number }; // Posição na tela (padrão: centro)
  width?: number; // Largura total do elemento em pixels (padrão: maxWidth)
  height?: number; // Altura total do elemento em pixels (padrão: auto)
  maxWidth?: number; // Largura máxima do container (padrão: 400px) - usado se width não for especificado
  padding?: number; // Padding interno (padrão: 20px)
  borderRadius?: number; // Raio da borda (padrão: 12px)
  lineHeight?: number; // Altura da linha (padrão: 1.4)
  textAlign?: 'left' | 'center' | 'right'; // Alinhamento do texto (padrão: 'center')
  zIndex?: number; // Z-index (padrão: 1000)
  // Novas props para animação typewriter
  typingSpeed?: number; // Velocidade de digitação em ms por caractere (padrão: 80)
  displayDuration?: number; // Tempo em segundos que o texto fica visível após completar (padrão: 5)
  fadeOutDuration?: number; // Duração do fade out em segundos (padrão: 1)
  cursorColor?: string; // Cor do cursor (padrão: mesma cor do texto)
  enableTypewriter?: boolean; // Habilitar animação typewriter (padrão: true)
  // Props para imagem de fundo
  backgroundImage?: string; // URL da imagem de fundo (será dimensionada para a largura do texto)
  backgroundImagePersistent?: boolean; // Se true, a imagem permanece visível mesmo quando o texto desaparece (padrão: false)
  backgroundImageOffsetY?: number; // Offset vertical da imagem em pixels (padrão: 0)
  backgroundImageOpacity?: number; // Opacidade da imagem de fundo (padrão: 1)
  // Props para imagens de canto (aparecem quando o texto está completo)
  cornerImage?: string; // URL da imagem que aparece nos cantos
  cornerImageSize?: number; // Tamanho da imagem de canto em pixels (padrão: 50)
  cornerImageOffsetX?: number; // Offset horizontal das imagens de canto (padrão: 20)
  cornerImageOffsetY?: number; // Offset vertical das imagens de canto (padrão: 20)
  cornerImageFadeInDuration?: number; // Duração do fade-in das imagens de canto em segundos (padrão: 0.5)
}

type AnimationPhase = 'typing' | 'display' | 'fadeOut' | 'hidden';

const CenterText: React.FC<CenterTextProps> = ({
  text,
  fontSize = 24,
  fontFamily = 'Arial, sans-serif',
  color = 'white',
  backgroundColor = 'rgba(0, 0, 0, 0.7)',
  position,
  width,
  height,
  maxWidth = 400,
  padding = 20,
  borderRadius = 12,
  lineHeight = 1.4,
  textAlign = 'center',
  zIndex = 1000,
  // Novas props com valores padrão
  typingSpeed = 80,
  displayDuration = 5,
  fadeOutDuration = 1,
  cursorColor,
  enableTypewriter = true,
  // Props de imagem de fundo
  backgroundImage,
  backgroundImagePersistent = false,
  backgroundImageOffsetY = 0,
  backgroundImageOpacity = 1,
  // Props de imagens de canto
  cornerImage,
  cornerImageSize = 50,
  cornerImageOffsetX = 20,
  cornerImageOffsetY = 20,
  cornerImageFadeInDuration = 0.5
}) => {
  // Estado para controlar a animação
  const [displayedText, setDisplayedText] = useState('');
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('typing');
  const [opacity, setOpacity] = useState(1);
  
  // Estado para controlar a opacidade das imagens de canto
  const [cornerImageOpacity, setCornerImageOpacity] = useState(0);
  
  // Ref para medir a largura do texto
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState<number>(0);

  // Texto puro sem marcações (para calcular o comprimento da animação)
  const plainText = useMemo(() => getPlainText(text), [text]);

  // Função para resetar a animação
  const resetAnimation = useCallback(() => {
    setDisplayedText('');
    setAnimationPhase('typing');
    setOpacity(1);
    setCornerImageOpacity(0);
  }, []);

  // Efeito para a animação de digitação
  useEffect(() => {
    if (!enableTypewriter) {
      setDisplayedText(plainText);
      setAnimationPhase('display');
      return;
    }

    if (animationPhase !== 'typing') return;

    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex < plainText.length) {
        setDisplayedText(plainText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setAnimationPhase('display');
      }
    }, typingSpeed);

    return () => clearInterval(typingInterval);
  }, [plainText, typingSpeed, animationPhase, enableTypewriter]);

  // Efeito para o tempo de exibição após completar a digitação
  useEffect(() => {
    if (animationPhase !== 'display') return;

    // Fade-in das imagens de canto quando o texto está completo
    if (cornerImage) {
      const startTime = Date.now();
      const fadeInInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = elapsed / (cornerImageFadeInDuration * 1000);
        
        if (progress >= 1) {
          clearInterval(fadeInInterval);
          setCornerImageOpacity(1);
        } else {
          setCornerImageOpacity(progress);
        }
      }, 16); // ~60fps

      // Limpar o intervalo quando mudar de fase
      const displayTimeout = setTimeout(() => {
        clearInterval(fadeInInterval);
        setAnimationPhase('fadeOut');
      }, displayDuration * 1000);

      return () => {
        clearInterval(fadeInInterval);
        clearTimeout(displayTimeout);
      };
    }

    const displayTimeout = setTimeout(() => {
      setAnimationPhase('fadeOut');
    }, displayDuration * 1000);

    return () => clearTimeout(displayTimeout);
  }, [animationPhase, displayDuration, cornerImage, cornerImageFadeInDuration]);

  // Efeito para o fade out
  useEffect(() => {
    if (animationPhase !== 'fadeOut') return;

    const startTime = Date.now();
    const fadeInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / (fadeOutDuration * 1000);
      
      if (progress >= 1) {
        clearInterval(fadeInterval);
        setOpacity(0);
        setCornerImageOpacity(0);
        setAnimationPhase('hidden');
      } else {
        setOpacity(1 - progress);
        setCornerImageOpacity(1 - progress);
      }
    }, 16); // ~60fps

    return () => clearInterval(fadeInterval);
  }, [animationPhase, fadeOutDuration]);

  // Efeito para reiniciar o loop após o fade out
  useEffect(() => {
    if (animationPhase !== 'hidden') return;

    // Pequeno delay antes de recomeçar
    const restartTimeout = setTimeout(() => {
      resetAnimation();
    }, 500);

    return () => clearTimeout(restartTimeout);
  }, [animationPhase, resetAnimation]);

  // Reset quando o texto muda
  useEffect(() => {
    resetAnimation();
  }, [text, resetAnimation]);

  // Efeito para medir a largura do texto
  useEffect(() => {
    if (textRef.current) {
      const width = textRef.current.offsetWidth;
      setTextWidth(width);
    }
  }, [plainText, fontSize, fontFamily]);

  // Calcular dimensões
  const finalWidth = width || maxWidth;
  const finalHeight = height || fontSize * lineHeight * 2 + padding * 2;

  // Calcular posição
  const finalPosition = position || {
    x: window.innerWidth / 2,
    y: (window.innerHeight / 2) - 15
  };

  // Verificar se o fundo é transparente para ajustar estilos
  const isTransparent = backgroundColor === 'transparent' || backgroundColor === 'rgba(0, 0, 0, 0)';

  // Determinar se deve mostrar o cursor
  const showCursor = enableTypewriter && animationPhase === 'typing';

  // Cor do cursor (usa a cor do texto se não especificada)
  const finalCursorColor = cursorColor || color;

  // Renderiza o texto com formatação aplicada ao texto exibido atualmente
  const renderFormattedText = useCallback((currentText: string): React.ReactNode => {
    // Parse do texto original para obter os segmentos formatados
    const segments = parseFormattedText(text);
    const result: React.ReactNode[] = [];
    let charCount = 0;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const segmentStart = charCount;
      const segmentEnd = charCount + segment.text.length;

      if (segmentStart >= currentText.length) {
        // Este segmento ainda não deve aparecer
        break;
      }

      // Calcula quantos caracteres deste segmento devem ser mostrados
      const visibleChars = Math.min(segment.text.length, currentText.length - segmentStart);
      const visibleText = segment.text.slice(0, visibleChars);

      if (visibleText) {
        if (segment.bold) {
          result.push(
            <strong key={i} style={{ fontWeight: '700' }}>
              {visibleText}
            </strong>
          );
        } else {
          result.push(<span key={i}>{visibleText}</span>);
        }
      }

      charCount = segmentEnd;
    }

    return result;
  }, [text]);

  return (
    <>
      {/* Imagem de fundo - posicionada fora do container para não ser cortada */}
      {backgroundImage && textWidth > 0 && (
        <img
          src={backgroundImage}
          alt=""
          style={{
            position: 'fixed',
            width: textWidth,
            height: 'auto',
            left: finalPosition.x,
            top: finalPosition.y - 20 + backgroundImageOffsetY,
            transform: 'translate(-50%, -50%)',
            zIndex: zIndex - 1,
            pointerEvents: 'none',
            opacity: backgroundImagePersistent 
              ? backgroundImageOpacity 
              : (animationPhase === 'hidden' ? 0 : opacity * backgroundImageOpacity),
            transition: backgroundImagePersistent ? 'none' : (animationPhase === 'hidden' ? 'none' : undefined),
          }}
        />
      )}
      {/* Imagem de canto superior esquerdo - posicionada relativa ao texto */}
      {cornerImage && textWidth > 0 && (
        <img
          src={cornerImage}
          alt=""
          style={{
            position: 'fixed',
            width: cornerImageSize,
            height: 'auto',
            left: finalPosition.x - textWidth / 2 + cornerImageOffsetX,
            top: finalPosition.y - finalHeight / 2 - 20 + cornerImageOffsetY,
            transform: 'translate(-50%, -50%)',
            zIndex: zIndex + 1,
            pointerEvents: 'none',
            opacity: cornerImageOpacity,
          }}
        />
      )}
      {/* Imagem de canto inferior direito - posicionada relativa ao texto */}
      {cornerImage && textWidth > 0 && (
        <img
          src={cornerImage}
          alt=""
          style={{
            position: 'fixed',
            width: cornerImageSize,
            height: 'auto',
            left: finalPosition.x + textWidth / 2 - cornerImageOffsetX,
            top: finalPosition.y + finalHeight / 2 - 20 - cornerImageOffsetY,
            transform: 'translate(-50%, -50%)',
            zIndex: zIndex + 1,
            pointerEvents: 'none',
            opacity: cornerImageOpacity,
          }}
        />
      )}
      <div
        style={{
          position: 'fixed',
          left: finalPosition.x - finalWidth / 2,
          top: finalPosition.y - finalHeight / 2 - 20,
          width: finalWidth,
          height: finalHeight,
          maxWidth: width ? 'none' : maxWidth,
          padding: padding,
          backgroundColor: backgroundColor,
          color: color,
          borderRadius: isTransparent ? 0 : borderRadius,
          zIndex: zIndex,
          pointerEvents: 'none',
          textAlign: textAlign,
          fontFamily: fontFamily,
          fontSize: fontSize,
          lineHeight: lineHeight,
          fontWeight: '300',
          boxShadow: isTransparent ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.3)',
          backdropFilter: isTransparent ? 'none' : 'blur(5px)',
          border: isTransparent ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          opacity: opacity,
          transition: animationPhase === 'hidden' ? 'none' : undefined,
        }}
      >
        {/* Container com largura fixa baseada no texto completo */}
      <span 
        className="center-text-content"
        style={{
          position: 'relative',
          display: 'inline-block',
        }}
      >
        {/* Texto invisível para reservar o espaço do texto completo */}
        <span ref={textRef} style={{ visibility: 'hidden' }}>
          {renderFormattedText(plainText)}
        </span>
        {/* Texto visível posicionado absolutamente da esquerda */}
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          {renderFormattedText(displayedText)}
          {showCursor && (
            <span 
              className="center-text-cursor"
              style={{ 
                color: finalCursorColor,
                borderColor: finalCursorColor
              }}
            >
              |
            </span>
          )}
        </span>
      </span>
    </div>
    </>
  );
};

export default CenterText;
